import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import "./FichaProductor.scss";
import Badge from "../../../components/common/Badge/Badge";
import TabsNavigation from "../../../components/common/TabsNavigation/TabsNavigation";
import Table from "../../../components/common/Table/Table";
import DetalleLayout from "../../../Layouts/DetalleLayout/DetalleLayout";
import { useZone } from "../../../context/ZoneContext";
import { getApiInstance } from "../../../utils/api";
import { mapList } from "../../../utils/utils";

const DOCUMENTOS_SEARCH_TERMS = [
    "tipoDocumento",
    "comunidad",
    "provincia",
    "año",
    "documento",
];

const DOCUMENTOS_FILTER_FIELDS = [
    {
        key: "codEstado",
        label: "Estado",
        type: "multiselect",
        labelMap: [
            "",
            "Pendiente de validar",
            "Validación con incidencias",
            "Validado",
            "Expirado",
        ],
    },
    {
        key: "tipoDocumento",
        label: "Tipo de documento",
        type: "multiselect",
    },
];

export default function FichaProductor({ user }) {
    const { id } = useParams();
    const [productor, setProductor] = useState(null);
    const [documentos, setDocumentos] = useState([]);
    const [cumplimientoLegal, setCumplimientoLegal] = useState([]);
    const [activeTabGeneral, setActiveTabGeneral] = useState("informacion");
    const [activeTabDatos, setActiveTabDatos] = useState("documentacion");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [comunidades, setComunidades] = useState([]);
    const [paises, setPaises] = useState([]);
    const [provincias, setProvincias] = useState([]);

    const fetchProductor = async (id) => {
        try {
            const api = getApiInstance();
            console.log(`Fetching gestor with ID: ${id}`);
            const response = await api.get(
                `/api/Productores/GetProductorById/${encodeURIComponent(id)}`
            );
            console.log(`Response from fetchGestor:`, response.data);
            return await response.data;
        } catch (error) {
            console.error("Error fetching gestor:", error);
            throw error;
        }
    };

    const fetchDocumentos = async (id) => {
        try {
            const api = getApiInstance();
            console.log(`Fetching gestor with ID: ${id}`);
            const response = await api.get(
                `/api/ProductoresDocumento/GetDocumentosFromProductor/${encodeURIComponent(id)}`
            );
            console.log(`Response from fetchGestor:`, response.data);
            return await response.data;
        } catch (error) {
            console.error("Error fetching gestor:", error);
            throw error;
        }
    };

    const handleDocumentosUpdate = useCallback(async () => {
        try {
            const data = await fetchDocumentos(id);
            setDocumentos(data);
        } catch (error) {
            console.error("Error refrescando documentos:", error);
        }
    }, [id]);

    const fetchCumplimientoLegal = async (id) => {
        try {
            const api = getApiInstance();
            console.log(`Fetching gestor with ID: ${id}`);
            const response = await api.post(
                `/api/CumplimientoLegal/GetCumplimientoByProductor/${encodeURIComponent(id)}`
            );
            console.log(`Response from fetchGestor:`, response.data);
            return await response.data;
        } catch (error) {
            console.error("Error fetching gestor:", error);
            throw error;
        }
    };

    const fetchComunidades = async () => {
        const api = getApiInstance();

        try {
            const response = await api.get(
                `/api/Comunidades/GetComunidades`
            );
            return response.data;
        } catch (err) {
            setError(
                "No se han podido cargar las comunidades. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchPaises = async () => {
        const api = getApiInstance();

        try {
            const response = await api.get(
                `/api/Paises/GetPaises`
            );
            return response.data;
        } catch (err) {
            setError(
                "No se han podido cargar los países. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchProvincias = async () => {
        const api = getApiInstance();
        try {
            const response = await api.get(
                `/api/Provincias/GetProvincias`
            );
            return response.data;
        } catch (err) {
            setError(
                "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setError(null);

        const loadAll = async () => {
            try {
                const productor = await fetchProductor(id);

                if (isMounted) {
                    setProductor(productor);
                }

                const results = await Promise.allSettled([
                    fetchDocumentos(id),
                    fetchCumplimientoLegal(id),
                    fetchPaises(),
                    fetchComunidades(),
                    fetchProvincias(),
                ]);

                const [
                    documentosResult,
                    cumplimientoLegalResult,
                    paisesResult,
                    comunidadesResult,
                    provinciasResult,
                ] = results;

                if (isMounted) {
                    if (documentosResult.status === "fulfilled") {
                        setDocumentos(documentosResult.value);
                    } else {
                        console.warn(
                            "No se pudieron cargar los documentos:",
                            documentosResult.reason
                        );
                    }

                    if (cumplimientoLegalResult.status === "fulfilled") {
                        setCumplimientoLegal(cumplimientoLegalResult.value);
                    } else {
                        console.warn(
                            "No se pudieron cargar las eléctricas:",
                            cumplimientoLegalResult.reason
                        );
                    }

                    if (paisesResult.status === "fulfilled") {
                        setPaises(
                            mapList(
                                paisesResult.value,
                                "pais",
                                "codPais",
                                "codPais"
                            )
                        );
                    } else {
                        console.warn(
                            "No se pudieron cargar las eléctricas:",
                            paisesResult.reason
                        );
                    }

                    if (comunidadesResult.status === "fulfilled") {
                        setComunidades([
                            ...mapList(
                                comunidadesResult.value,
                                "comunidad",
                                "codComunidad",
                                "codComunidad",
                                "codPais"
                            ),
                        ]);
                    } else {
                        console.warn(
                            "No se pudieron cargar las eléctricas:",
                            comunidadesResult.reason
                        );
                    }

                    if (provinciasResult.status === "fulfilled") {
                        setProvincias([
                            ...mapList(
                                provinciasResult.value,
                                "provincia",
                                "codProvincia",
                                "codProvincia",
                                "codComunidad"
                            ),
                        ]);
                    } else {
                        console.warn(
                            "No se pudieron cargar las eléctricas:",
                            provinciasResult.reason
                        );
                    }
                }
            } catch (err) {
                if (isMounted) {
                    setError(
                        "No se han podido cargar los datos principales. Vuelve a intentarlo más tarde."
                    );
                }
                console.error("Error en datos críticos de loadAll:", err);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        loadAll();

        return () => {
            isMounted = false;
        };
    }, [id]);

    const getEstadoBadge = (estado) => {
        const estadoConfig = {
            VALIDADO: { type: "success", label: "Validado" },
            PENDIENTE: { type: "info", label: "Pendiente" },
            INCIDENCIA: { type: "warning", label: "Incidencia" },
            VENCIDO: { type: "error", label: "Vencido" },
            ACTIVA: { type: "success", label: "Activa" },
        };
        return estadoConfig[estado] || estadoConfig["PENDIENTE"];
    };

    const tabsGeneral = [
        { id: "informacion", label: "Información General" },
        { id: "ubicacion", label: "Ubicación" },
    ];

    const tabsDatos = [
        { id: "documentacion", label: "Documentación" },
        { id: "cumplimiento", label: "Cumplimiento legal" },
    ];

    const renderInformacionGeneral = () => (
        <div className="ficha-gestor__info">
            <div className="ficha-gestor__field">
                <label>Nombre:</label>
                <span>{productor.nombre}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>CIF:</label>
                <span>{productor.cif}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Contacto:</label>
                <span>{productor.contacto}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Teléfono:</label>
                <span>{productor.telefono}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Email:</label>
                <span>{productor.eMail}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Nombre representante:</label>
                <span>{productor.nombreRepresentante}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Apellidos representante:</label>
                <span>{productor.apellidosRepresentante}</span>
            </div>
        </div>
    );

    const renderLocalizacion = () => (
        <div className="ficha-gestor__info">
            <div className="ficha-gestor__field">
                <label>Dirección:</label>
                <span>{productor.direccion}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Localidad:</label>
                <span>{productor.localidad}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Provincia:</label>
                <span>{productor.provincia}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Comunidad:</label>
                <span>{productor.comunidad}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>País:</label>
                <span>{productor.pais}</span>
            </div>
        </div>
    );

    const renderGeneralContent = () => {
        switch (activeTabGeneral) {
            case "informacion":
                return renderInformacionGeneral();
            case "ubicacion":
                return renderLocalizacion();
            default:
                return renderInformacionGeneral();
        }
    };

    const renderDatosContent = () => {
        switch (activeTabDatos) {
            case "documentacion":
                return (
                    <div className="ficha-gestor__documentacion">
                        <Table
                            columns={[
                                {
                                    key: "tipoDocumento",
                                    label: "Tipo de documento",
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "comunidad",
                                    label: "Comunidad",
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "provincia",
                                    label: "Provincia",
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "año",
                                    label: "Año",
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "documento",
                                    label: "Documento",
                                    visible: true,
                                    sortable: true,
                                    documentProp: "ficheroBase64",
                                    type: "document",
                                },
                                {
                                    key: "codEstado",
                                    label: "Estado",
                                    visible: true,
                                    sortable: true,
                                },
                            ]}
                            dataTable={documentos}
                            onDataUpdate={handleDocumentosUpdate}
                            keyProp="codDocumento"
                            searchTerms={DOCUMENTOS_SEARCH_TERMS}
                            filterFields={DOCUMENTOS_FILTER_FIELDS}
                            noResultsMessage="No hay documentos disponibles"
                            rowsPerPage={10}
                            showCheckboxes={false}
                            actions={["Eliminar"]}
                            elementName="Documento"
                            stateProp="codEstado"
                            states={[
                                { type: "primary", label: "" },
                                { type: "info", label: "Pendiente de validar" },
                                {
                                    type: "warning",
                                    label: "Validación con incidencias",
                                },
                                { type: "success", label: "Validado" },
                                { type: "danger", label: "Expirado" },
                            ]}
                            formElements={[
                                {
                                    elements: [
                                        {
                                            name: "codTipoDocumento",
                                            type: "select",
                                            label: "Tipo de documento",
                                            placeholder:
                                                "Seleccione el tipo de documento",
                                            required: true,
                                            disabled: false,
                                            labelVisible: true,
                                            visible: true,
                                            options: [
                                                {
                                                    value: 2,
                                                    label: "Alta pequeño productor",
                                                },
                                                {
                                                    value: 3,
                                                    label: "Declaración anual",
                                                },
                                            ],
                                        },
                                        {
                                            name: "año",
                                            type: "number",
                                            label: "Año",
                                            placeholder: "Ingrese el año",
                                            required: true,
                                            disabled: false,
                                            labelVisible: true,
                                            visible: true,
                                            visibleWhen: {
                                                field: "codTipoDocumento",
                                                value: 3,
                                            },
                                        },
                                        {
                                            name: "codPais",
                                            type: "select",
                                            label: "País",
                                            placeholder: "Seleccione el país",
                                            options: paises,
                                            required: true,
                                            disabled: false,
                                            labelVisible: true,
                                            visible: true,
                                        },
                                        {
                                            name: "codComunidad",
                                            type: "select",
                                            label: "Comunidad",
                                            placeholder:
                                                "Seleccione la comunidad",
                                            options: comunidades,
                                            required: true,
                                            disabled: false,
                                            labelVisible: true,
                                            visible: true,
                                            associatedFormElement: "codPais",
                                        },
                                        {
                                            name: "codProvincia",
                                            type: "select",
                                            label: "Provincia",
                                            placeholder:
                                                "Seleccione la provincia",
                                            options: provincias,
                                            required: true,
                                            disabled: false,
                                            labelVisible: true,
                                            visible: true,
                                            associatedFormElement:
                                                "codComunidad",
                                        },
                                        {
                                            name: "codProductor",
                                            type: "number",
                                            label: "CodProductor",
                                            visible: false,
                                            defaultValue:
                                                productor?.codProductor ?? "",
                                        },
                                        {
                                            name: "codEstado",
                                            type: "number",
                                            label: "CodEstado",
                                            visible: false,
                                            defaultValue: 1,
                                        },
                                        {
                                            name: "fichero",
                                            type: "document",
                                            label: "Documento",
                                            placeholder: "Subir documento",
                                            required: true,
                                            disabled: false,
                                            labelVisible: true,
                                            visible: true,
                                        },
                                        {
                                            name: "obs",
                                            type: "textarea",
                                            label: "Observaciones",
                                            placeholder:
                                                "Ingrese observaciones adicionales",
                                            required: false,
                                            disabled: false,
                                            labelVisible: true,
                                            visible: true,
                                            rows: 3,
                                        },
                                    ],
                                },
                            ]}
                            addApiUrl="/api/ProductoresDocumento/SetDocumento"
                            deleteApiUrl="/api/ProductoresDocumento/DeleteDocumento"
                            loading={loading}
                            userCodAcceso={Number(user.codAcceso)}
                            userCodPerfil={Number(user.role)}
                        />
                    </div>
                );
            case "cumplimiento":
                return (
                    <div className="ficha-gestor__tarifas">
                        <Table
                            columns={[
                                {
                                    key: "nombre",
                                    label: "Descripción",
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "comunidad",
                                    label: "Comunidad",
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "año",
                                    label: "Año",
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "codEstado",
                                    label: "Estado",
                                    visible: true,
                                    sortable: true,
                                },
                            ]}
                            dataTable={cumplimientoLegal}
                            noResultsMessage="No hay cumplimientos legales disponibles"
                            rowsPerPage={10}
                            showCheckboxes={false}
                            elementName="Cumplimiento legal"
                            stateProp="codEstado"
                            states={[
                                { type: "primary", label: "" },
                                { type: "danger", label: "No presentado" },
                                {
                                    type: "warning",
                                    label: "Validado con incidencias",
                                },
                                { type: "success", label: "Validado" },
                                { type: "danger", label: "Expirado" },
                            ]}
                            userCodAcceso={Number(user.codAcceso)}
                            userCodPerfil={Number(user.role)}
                            loading={loading}
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <DetalleLayout
            title={productor ? `Productor ${productor.nombre}` : "Cargando..."}
            backLink="/administracion/productores"
            backText="Volver a Productores"
            loading={loading}
            error={error}
            notFound={!productor && !loading && !error}
            className="ficha-gestor"
        >
            {!loading && !error && productor && (
                <div className="ficha-gestor__content">
                    <div className="ficha-gestor__section">
                        <div className="ficha-gestor__section-header">
                            <h2>Información General</h2>
                        </div>

                        <TabsNavigation
                            tabs={tabsGeneral}
                            activeTab={activeTabGeneral}
                            onTabChange={setActiveTabGeneral}
                        />

                        <div className="ficha-gestor__section-content">
                            {renderGeneralContent()}
                        </div>
                    </div>

                    <div className="ficha-gestor__section">
                        <div className="ficha-gestor__section-header">
                            <h2>Datos del productor</h2>
                        </div>

                        <TabsNavigation
                            tabs={tabsDatos}
                            activeTab={activeTabDatos}
                            onTabChange={setActiveTabDatos}
                        />

                        <div className="ficha-gestor__section-content">
                            {renderDatosContent()}
                        </div>
                    </div>
                </div>
            )}
        </DetalleLayout>
    );
}
